// AttachExistingTicketModal — link an existing ticket as a child of the current ticket.
//
// UX: single debounced searchable field (ticket number + subject); agents never enter
// internal DB ids. Selection uses ticketId only in the attach API payload.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Link2, Loader2, AlertCircle, Search, ChevronDown } from 'lucide-react';
import { ticketService, isTicketApiSuccess } from '../../api/ticketService';

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;
const SEARCH_PAGE_SIZE = 20;

const errorMessageFor = (err) => {
  const fromResponse = err?.response?.data?.message || err?.data?.message;
  if (fromResponse) return fromResponse;
  if (err?.message) return err.message;
  return 'Unable to attach child ticket.';
};

const AttachExistingTicketModal = ({ parentTicketId, parentTicketNumber, isOpen, onClose, onAttached }) => {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const listboxId = 'attach-child-linkable-listbox';

  const reset = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setSearchText('');
    setResults([]);
    setSearchLoading(false);
    setSearchError(null);
    setSelected(null);
    setSubmitting(false);
    setFormError(null);
    setDropdownOpen(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const runSearch = useCallback(
    async (q) => {
      const trimmed = (q || '').trim();
      if (trimmed.length < MIN_CHARS) {
        setResults([]);
        setSearchLoading(false);
        setSearchError(null);
        return;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const ac = new AbortController();
      abortRef.current = ac;
      setSearchLoading(true);
      setSearchError(null);
      try {
        const res = await ticketService.searchLinkableRelationTickets(parentTicketId, {
          query: trimmed,
          page: 0,
          size: SEARCH_PAGE_SIZE,
          signal: ac.signal,
        });
        if (abortRef.current !== ac) return;
        const apiData = res?.data;
        if (!isTicketApiSuccess(res)) {
          throw new Error(apiData?.message || 'Search failed');
        }
        const page = apiData?.data;
        const items = Array.isArray(page?.items) ? page.items : [];
        setResults(items);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setResults([]);
        setSearchError(errorMessageFor(err));
      } finally {
        if (abortRef.current === ac) {
          setSearchLoading(false);
        }
      }
    },
    [parentTicketId],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    setSearchText('');
    setResults([]);
    setSearchLoading(false);
    setSearchError(null);
    setSelected(null);
    setFormError(null);
    setDropdownOpen(false);
  }, [isOpen, parentTicketId]);

  useEffect(() => {
    if (!isOpen) return;
    const q = searchText;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if ((q || '').trim().length < MIN_CHARS) {
      setResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      runSearch(q);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [searchText, isOpen, runSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!selected?.ticketId) {
      setFormError('Search and select a ticket to attach.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await ticketService.attachExistingChild(parentTicketId, {
        childTicketId: selected.ticketId,
      });
      const apiData = res?.data;
      if (apiData?.success === false) {
        throw new Error(apiData?.message || 'Attach failed');
      }
      if (!isTicketApiSuccess(res)) {
        throw new Error(apiData?.message || 'Attach failed');
      }
      reset();
      onAttached?.(apiData?.data || apiData);
      onClose();
    } catch (err) {
      setFormError(errorMessageFor(err));
      setSubmitting(false);
    }
  };

  const pickResult = (row) => {
    setSelected(row);
    setSearchText(`${row.ticketNumber} — ${row.subject || ''}`.trim());
    setDropdownOpen(false);
    setFormError(null);
  };

  const clearSelection = () => {
    setSelected(null);
    setSearchText('');
    setResults([]);
    setDropdownOpen(true);
  };

  if (!isOpen) return null;

  const trimmedSearch = searchText.trim();
  const showDropdown =
    dropdownOpen && (trimmedSearch.length >= MIN_CHARS || searchLoading || results.length > 0 || searchError);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Attach existing ticket as child
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Link an existing ticket as a child of{' '}
            <span className="font-mono font-medium">{parentTicketNumber || `#${parentTicketId}`}</span>.
            Search by ticket number or subject, then select a result. Both tickets stay independent for
            status, assignment, and SLA.
          </p>

          <div className="relative">
            <label htmlFor="attach-child-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Find ticket
            </label>
            <div className="mt-1 relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                id="attach-child-search"
                type="text"
                role="combobox"
                aria-expanded={dropdownOpen}
                aria-controls={listboxId}
                aria-autocomplete="list"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setSelected(null);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                disabled={submitting}
                placeholder="e.g. TKT-000031 or keywords from the subject"
                autoComplete="off"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2 pl-9 pr-9 text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              At least {MIN_CHARS} characters. Results update after you stop typing (~{DEBOUNCE_MS} ms).
            </p>

            {showDropdown && (
              <ul
                id={listboxId}
                role="listbox"
                className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 py-1 text-sm shadow-lg"
              >
                {searchLoading && (
                  <li className="px-3 py-2 text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    Searching…
                  </li>
                )}
                {!searchLoading && searchError && (
                  <li className="px-3 py-2 text-red-600 dark:text-red-400">{searchError}</li>
                )}
                {!searchLoading &&
                  !searchError &&
                  trimmedSearch.length >= MIN_CHARS &&
                  results.length === 0 && (
                    <li className="px-3 py-2 text-gray-500 dark:text-gray-400">No matching linkable tickets.</li>
                  )}
                {!searchLoading &&
                  !searchError &&
                  results.map((row) => (
                    <li key={row.ticketId} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected?.ticketId === row.ticketId}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 focus:bg-indigo-50 dark:focus:bg-indigo-900/30 focus:outline-none"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickResult(row)}
                      >
                        <div className="font-mono text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                          {row.ticketNumber}
                        </div>
                        <div className="text-gray-800 dark:text-gray-200 line-clamp-2">{row.subject}</div>
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {[row.statusName || row.statusCode, row.priorityName].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {selected && (
            <div className="rounded-md border border-indigo-200 dark:border-indigo-800 bg-indigo-50/80 dark:bg-indigo-950/40 px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <div>
                  <div className="font-mono text-xs font-semibold text-indigo-800 dark:text-indigo-200">
                    {selected.ticketNumber}
                  </div>
                  <div className="text-gray-800 dark:text-gray-100">{selected.subject}</div>
                </div>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={submitting}
                  className="shrink-0 text-xs text-indigo-700 dark:text-indigo-300 hover:underline disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {formError && (
            <div className="flex items-start gap-2 rounded border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="break-words">{formError}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selected}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Attach as child
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttachExistingTicketModal;
